import { useEffect, useState, useCallback } from "react";
import { Sparkles, X, Loader2, AlertCircle, Clock } from "lucide-react";
import { axiosInstance } from "../../lib/axios";
import { useChatStore } from "../../store/useChatStore";
import { useAuthStore } from "../../store/useAuthStore";

// Compact AI Communication Assistant above the composer: a short summary, up to
// two suggested replies, and the top pending actions. Collapses to a small pill
// so it never dominates the conversation — click the pill to reopen.
export default function InboxAIAssistant({ projectName }: { projectName: string }) {
  const { messages, selectedUser, sendMessage } = useChatStore();
  const { authUser }: any = useAuthStore();
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{ summary: string; suggestedReplies: string[]; pendingActions: string[] } | null>(null);

  const analyze = useCallback(async () => {
    if (!selectedUser || messages.length === 0) { setData(null); return; }
    setLoading(true);
    try {
      const payload = messages.slice(-30).map((m: any) => ({
        sender: m.senderId === authUser?._id ? "me" : "client",
        text: m.text || (m.file ? `[file: ${m.file.fileName}]` : ""),
      }));
      const { data } = await axiosInstance.post("/ai/conversation-assistant", { messages: payload, projectName });
      setData({ summary: data.summary, suggestedReplies: data.suggestedReplies || [], pendingActions: data.pendingActions || [] });
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [messages, selectedUser, authUser, projectName]);

  // Re-analyze when the conversation or project changes (debounced).
  useEffect(() => {
    const t = setTimeout(analyze, 600);
    return () => clearTimeout(t);
  }, [analyze]);

  if (!selectedUser) return null;

  // Collapsed → small reopen pill.
  if (!open) {
    return (
      <div className="mb-2">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/30 bg-primary-container/10 text-primary text-[12px] font-semibold hover:bg-primary-container/20 transition-colors"
        >
          <Sparkles size={14} fill="currentColor" /> AI Assistant
        </button>
      </div>
    );
  }

  const replies = (data?.suggestedReplies || []).slice(0, 2);
  const actions = (data?.pendingActions || []).slice(0, 3);

  return (
    <div className="mb-3 bg-white border border-primary/20 rounded-xl px-3.5 py-2.5 shadow-sm relative overflow-hidden">
      <div className="absolute inset-y-0 left-0 w-1 bg-primary" />
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Sparkles size={15} className="text-primary" fill="currentColor" />
          <span className="text-[11px] font-bold text-primary uppercase tracking-tight">AI Communication Assistant</span>
        </div>
        <button onClick={() => setOpen(false)} title="Hide" className="text-outline hover:text-on-surface transition-colors"><X size={15} /></button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-on-surface-variant text-[12px] py-0.5"><Loader2 className="animate-spin text-primary" size={14} /> Analyzing…</div>
      ) : !data ? (
        <p className="text-[12px] text-on-surface-variant">No conversation to analyze yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
          <div className="min-w-0">
            <p className="text-[10px] text-outline font-semibold uppercase tracking-wide mb-0.5">Summary</p>
            <p className="text-[12px] text-on-surface-variant line-clamp-2">{data.summary}</p>
          </div>
          {replies.length > 0 && (
            <div className="min-w-0">
              <p className="text-[10px] text-outline font-semibold uppercase tracking-wide mb-1">Suggested Replies</p>
              <div className="flex flex-col gap-1">
                {replies.map((r, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage({ text: r } as any)}
                    title="Click to send"
                    className={`text-left px-2.5 py-1 rounded-lg text-[12px] leading-snug transition-all line-clamp-2 ${i === 0 ? "bg-primary-container/10 border border-primary/20 hover:border-primary text-primary" : "bg-white border border-outline-variant hover:border-primary text-on-surface"}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
          {actions.length > 0 && (
            <div className="md:col-span-2 border-t border-outline-variant/30 pt-1.5">
              <div className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                <span className="text-[10px] text-primary font-bold uppercase tracking-wide">Pending</span>
                {actions.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[12px] text-on-surface-variant">
                    {i === 0 ? <AlertCircle size={13} className="text-error shrink-0" /> : <Clock size={13} className="text-outline shrink-0" />}
                    <span className="truncate max-w-[240px]">{a}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
